/**
 * Builds HTML from plaintext terms (numbered sections like "4. Title", subs "4.1 Title",
 * bullets with • , - , or leading "o "), plus **inline bold** Markdown.
 */

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inlineBoldEscaped(escapedPlain: string): string {
  return escapedPlain.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

function classifyLine(trimmed: string): "empty" | "title" | "h2" | "h3" | "bullet" | "body" {
  if (!trimmed) return "empty";
  if (/^TERMS\s+AND\s+CONDITIONS\b/i.test(trimmed)) return "title";
  if (/^\d+\.\d+(?:\.\d+)?\s+\S/.test(trimmed)) return "h3";
  if (/^\d+\.\s+\S/.test(trimmed)) return "h2";

  const ch = trimmed[0];
  if ("•●◦‧·○".includes(ch)) return "bullet";
  if (/^[-–−]\s+\S/u.test(trimmed)) return "bullet";
  if (/^o[\s.\t]+\S/i.test(trimmed)) return "bullet";

  return "body";
}

function stripBulletPrefix(trimmed: string): string {
  return trimmed
    .replace(/^[•●◦‧·○]\s*\t*/u, "")
    .replace(/^[-–−]\s+\t*/u, "")
    .replace(/^o[\s.\t]+/i, "")
    .trim();
}

/** Turn raw fetched terms content into sanitized HTML fragments (no wrapping root element). */
export function formatTermsToHtml(raw: string): string {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const fragments: string[] = [];
  const bodyParagraphLines: string[] = [];
  const bulletLines: string[] = [];

  const flushBodyParagraph = (): void => {
    if (!bodyParagraphLines.length) return;
    const escaped = escapeText(bodyParagraphLines.join(" ").replace(/\s+/g, " ").trim());
    fragments.push(`<p class="terms-body">${inlineBoldEscaped(escaped)}</p>`);
    bodyParagraphLines.length = 0;
  };

  const flushBullets = (): void => {
    flushBodyParagraph();
    if (!bulletLines.length) return;
    const lis = bulletLines
      .map((t) => {
        const stripped = escapeText(stripBulletPrefix(t).replace(/\s+/g, " ").trim());
        return `<li>${inlineBoldEscaped(stripped)}</li>`;
      })
      .join("");
    fragments.push(`<ul class="terms-bullet-list">${lis}</ul>`);
    bulletLines.length = 0;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const kind = classifyLine(trimmed);

    if (kind === "empty") {
      flushBullets();
      flushBodyParagraph();
      continue;
    }

    if (kind === "title") {
      flushBullets();
      flushBodyParagraph();
      fragments.push(
        `<h1 class="terms-doc-title">${inlineBoldEscaped(escapeText(trimmed))}</h1>`
      );
      continue;
    }

    if (kind === "h3") {
      flushBullets();
      flushBodyParagraph();
      fragments.push(
        `<h3 class="terms-subsection">${inlineBoldEscaped(
          escapeText(trimmed.replace(/\s+/g, " ").trim())
        )}</h3>`
      );
      continue;
    }

    if (kind === "h2") {
      flushBullets();
      flushBodyParagraph();
      fragments.push(
        `<h2 class="terms-section">${inlineBoldEscaped(
          escapeText(trimmed.replace(/\s+/g, " ").trim())
        )}</h2>`
      );
      continue;
    }

    if (kind === "bullet") {
      flushBodyParagraph();
      bulletLines.push(trimmed);
      continue;
    }

    flushBullets();
    bodyParagraphLines.push(trimmed);
  }

  flushBullets();
  flushBodyParagraph();

  return fragments.join("\n");
}